'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import useDimensions from 'react-cool-dimensions';
import { ResizeObserver } from '@juggle/resize-observer';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { usePageScroll } from '../page-scroll-provider/page-scroll-provider';

type DarkCircleBackgroundProps = {
    children: JSX.Element | JSX.Element[];
};

export const DarkCircleBackground = (props: DarkCircleBackgroundProps) => {
    const { children } = props;
    const pageScroll = usePageScroll();
    // useReducedMotion returns null on server/initial render, default to false
    const prefersReducedMotion = useReducedMotion();
    const shouldReduceMotion = prefersReducedMotion === true;
    const updateDarkCircle = useMemo(() => {
        // Provide a no-op function if pageScroll is not available
        const noop = () => {
            /* no-op placeholder */
        };
        return pageScroll?.updateDarkCircle || noop;
    }, [pageScroll?.updateDarkCircle]);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerElementRef = useRef<HTMLDivElement>(null);
    const scrollTargetRef = useRef<HTMLDivElement>(null);
    const [clipPath, setClipPath] = useState<string>('circle(0px at 50% 50%)');
    const { observe: containerRef } = useDimensions<HTMLDivElement | null>({ polyfill: ResizeObserver });
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Track scroll progress
    const { scrollYProgress } = useScroll({
        target: scrollTargetRef,
        offset: ['start end', 'end start'],
    });

    // Mobile reaches full scale earlier to prevent content clipping
    const expandAt = isMobile ? 0.15 : 0.3;
    const scaleValues = shouldReduceMotion ? [0.95, 1.05, 1.05, 0.95] : [0.5, 1.2, 1.2, 0];
    const opacityValues = shouldReduceMotion ? [1, 1] : [0, 1];

    const scale = useTransform(scrollYProgress, [0, expandAt, 0.8, 1], scaleValues);
    const contentOpacity = useTransform(scrollYProgress, [0, expandAt], opacityValues);

    // Setup and draw canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerElementRef.current;

        if (!canvas || !container) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;

        const setupCanvas = () => {
            canvas.width = window.innerWidth * dpr;
            canvas.height = window.innerHeight * dpr;
            ctx.scale(dpr, dpr);
        };

        const drawCircle = () => {
            // Clear canvas
            ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

            // Get container position
            const rect = container.getBoundingClientRect();

            // Circle radius: use the larger of width/2 or height/2 to ensure coverage
            const baseRadius = Math.max(rect.width / 2, rect.height / 2);
            const currentScale = scale.get();
            const radius = baseRadius * currentScale;

            // Horizontally centered on container
            const centerX = rect.left + rect.width / 2;

            // Fixed at center of container
            const centerY = rect.top + rect.height / 2;

            // Update context with circle position
            updateDarkCircle({
                centerX,
                centerY,
                radius,
                containerTop: rect.top,
                containerBottom: rect.bottom,
            });

            // Update clip-path to match circle
            // Calculate center position relative to the content container (scrollTargetRef)
            const scrollTarget = scrollTargetRef.current;
            if (scrollTarget) {
                const scrollRect = scrollTarget.getBoundingClientRect();
                // Convert viewport coordinates to element-relative coordinates
                const clipCenterX = centerX - scrollRect.left;
                const clipCenterY = centerY - scrollRect.top;
                setClipPath(`circle(${radius}px at ${clipCenterX}px ${clipCenterY}px)`);
            }

            // Draw circle
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            ctx.fillStyle = '#232628';
            ctx.fill();
        };

        // Setup
        setupCanvas();

        // Draw on scroll
        const handleScroll = () => {
            requestAnimationFrame(drawCircle);
        };

        // Draw on resize
        const handleResize = () => {
            setupCanvas();
            drawCircle();
        };

        // Listen to scale changes
        const unsubscribe = scale.on('change', () => {
            requestAnimationFrame(drawCircle);
        });

        // Initial draw
        drawCircle();

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize);

        return () => {
            unsubscribe();
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [scale, updateDarkCircle]);

    return (
        <>
            {/* Fixed canvas covering viewport */}
            <canvas ref={canvasRef} className="fixed top-0 left-0 z-20 w-screen h-screen pointer-events-none" />
            {/* Content container */}
            <div className="relative z-30 my-[240px]">
                <div
                    ref={scrollTargetRef}
                    className="relative min-h-screen"
                    style={{
                        clipPath: clipPath,
                    }}
                >
                    <motion.div
                        ref={(el) => {
                            containerRef(el);
                            containerElementRef.current = el;
                        }}
                        className="relative w-full mx-auto max-w-container"
                        style={{ opacity: contentOpacity }}
                    >
                        <div className="flex flex-col justify-center min-h-screen md:grid md:grid-cols-14 md:place-content-center">
                            <div className="col-span-12 col-start-2">{children}</div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </>
    );
};

export default DarkCircleBackground;
